import { SxProps, Theme } from '@mui/material';

/* Sticky-right styling for a pinned column: keeps it in view while the table scrolls
 * horizontally. The divider and its depth cue are pseudo-elements rather than a border and
 * a box-shadow, which a table cell paints at its unscrolled position instead of carrying
 * along as it sticks. Both only apply while the container is marked as having columns
 * hidden to the right, so a table that fits, or one scrolled fully right, reads as a plain
 * column. The cue is darker in dark mode, where a light-mode black wash reads as nothing.
 */
export const makePinnedRightSx =
    (zIndex: number, background: 'paper' | 'row' = 'paper'): SxProps<Theme> =>
    (theme) => ({
        position: 'sticky',
        right: 0,
        ...(background === 'row'
            ? { backgroundColor: 'inherit', backgroundImage: 'inherit' }
            : { backgroundColor: theme.palette.background.paper }),
        '[data-pinned-overflow="true"] &::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '1px',
            backgroundColor: theme.palette.divider,
            pointerEvents: 'none',
        },
        '[data-pinned-overflow="true"] &::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: '100%',
            width: '6px',
            background: `linear-gradient(to left, ${
                theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.09)'
            }, transparent)`,
            pointerEvents: 'none',
        },
        zIndex,
    });

// The header cell sits above its own column and the sibling headers.
export const pinnedRightCellSx = makePinnedRightSx(2);
export const pinnedRightRowCellSx = makePinnedRightSx(2, 'row');
export const pinnedRightHeaderSx = makePinnedRightSx(3);
